import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Lottie from "lottie-react";
import { AlertTriangle, RefreshCcw } from "lucide-react";
import animationData from "../assets/animation/error12.json";
import AnimatedBackground from "../components/accueil/AnimatedBackground";

interface PageErreurProps {
    error?: Error | null;
    resetErrorBoundary?: () => void;
}

/**
 * PageErreur - A premium, universal error page.
 * Handles 404s, runtime errors, and provides automatic redirection.
 */
const PageErreur: React.FC<PageErreurProps> = ({ error, resetErrorBoundary }) => {
    const navigate = useNavigate();
    const [countdown, setCountdown] = useState(5);

    useEffect(() => {
        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    navigate("/");
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [navigate]);

    return (

        <div className="min-h-screen bg-[#050505] flex flex-col items-center space-y-6 justify-start p-6 font-sans selection:bg-purple-500/30">


            {/* ================================================ */}
            {/* BACKGROUND GLOBAL AVEC LES ÉLÉMENTS TECH */}
            {/* ================================================ */}
            <AnimatedBackground variant="global" showNeuralNodes={true} showParticles={true} />



            {/* Background Decorative Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-purple-600/10 blur-[120px] rounded-full" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-600/10 blur-[120px] rounded-full" />
            </div>



            <div className="relative z-10 flex flex-col items-center text-center max-w-2xl w-full">
                {/* Lottie Animation Container */}
                <div className="w-full max-w-xl md:max-w-2xl h-[200px] md:h-[500px] aspect-square mb-0 drop-shadow-[0_0_30px_rgba(168,85,247,0.2)]">
                    <Lottie
                        animationData={animationData}
                        loop={true}
                        color={"#ffffff"}
                        className="w-full h-full text-white"
                    />
                </div>

                {/* Error Content */}
                <div className="space-y-4">
                    <div className="inline-flex items-center px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 text-sm font-medium mb-2">
                        <AlertTriangle className="w-4 h-4 mr-2" />
                        {error ? "Erreur Système Détectée" : "Page Introuvable"}
                    </div>

                    <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight">
                        {error ? "Oups ! Quelque chose a mal tourné." : "Perdu dans le vide cosmique ?"}
                    </h1>

                    <p className="text-gray-400 text-lg md:text-xl leading-relaxed max-w-lg mx-auto">
                        {error
                            ? "Une erreur inattendue est survenue dans l'application. Nos ingénieurs galactiques sont sur le coup."
                            : "La page que vous recherchez semble avoir disparu ou l'URL est incorrecte."}
                    </p>

                    <div className="pt-4 flex flex-col items-center gap-4">
                        <p className="text-2xl text-gray-500 flex items-center gap-2">
                            <span className="relative flex h-4 w-4">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-4 w-4 bg-purple-500"></span>
                            </span>
                            Redirection automatique dans <span className="text-purple-400 font-bold tabular-nums">{countdown}s</span>
                        </p>

                        <div className="flex flex-wrap items-center justify-center gap-4 mt-2">


                            {/* <button
                                onClick={() => navigate("/")}
                                className="w-[280px] sm:w-[280px] group relative cursor-pointer px-6 py-3 sm:px-8 sm:py-3 bg-purple-600 hover:bg-purple-500 text-white text-[14px] sm:text-lg font-bold rounded-full overflow-hidden transition-all duration-300 shadow-[0_10px_40px_rgba(37,99,235,0.3)] hover:shadow-[0_20px_60px_rgba(37,99,235,0.5)] hover:-translate-y-1"
                            >
                                <div className="absolute inset-0 bg-gradient-to-r from-purple-400/0 via-white/80 to-purple-400/0 translate-x-[-150%] group-hover:translate-x-[150%] transition-transform duration-700 ease-in-out" />
                                <span className="relative flex items-center justify-center gap-2 sm:gap-3">
                                    Retourner à l'accueil
                                    <Home className="w-4 h-4 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" />
                                </span>
                            </button> */}

                            {error && resetErrorBoundary && (
                                <button
                                    onClick={resetErrorBoundary}
                                    className="flex items-center gap-2 px-8 py-3 bg-white/5 text-white font-semibold rounded-xl border border-white/10 transition-all duration-300 hover:bg-white/10"
                                >
                                    <RefreshCcw className="w-5 h-5" />
                                    Réessayer
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Debug Info (Only if error exists and in dev - optional) */}
                {error && process.env.NODE_ENV === 'development' && (
                    <div className="mt-12 p-4 bg-red-950/20 border border-red-500/20 rounded-lg text-left w-full overflow-hidden">
                        <p className="text-red-400 text-xs font-mono break-all">{error.message}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PageErreur;
